import type { CSSProperties } from 'react';

interface BrandMarkProps {
  /** Square size in px. 36 in the top bar, 32 in compact headers. */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * The UniPostudio mark (2026-11 logo): post cards for every network stacked
 * behind one composer card carrying the U, whose shorter right arm
 * makes room for the amber AI spark. The same
 * drawing as the favicon (apps/web/index.html), the marketing site and the
 * email header. Decorative — the button or link around it carries the
 * accessible name. The gradient id is shared on purpose: every copy defines
 * the same gradient, so duplicates on one page are harmless.
 */
export function BrandMark({ size = 36, className = '', style }: BrandMarkProps): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      className={['inline-block shrink-0 select-none', className].join(' ')}
      style={style}
    >
      <defs>
        <linearGradient id="unipostudio-mark-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#63408C" />
          <stop offset="1" stopColor="#36204F" />
        </linearGradient>
      </defs>
      <rect width="1024" height="1024" rx="240" fill="url(#unipostudio-mark-bg)" />
      <g transform="translate(10,8)">
        <rect x="332" y="264" width="400" height="400" rx="92" fill="#fff" opacity=".18" />
        <rect x="302" y="304" width="400" height="400" rx="92" fill="#fff" opacity=".38" />
        <rect x="272" y="344" width="400" height="400" rx="92" fill="#fff" />
        <path d="M384 456 V574 A88 88 0 0 0 560 574 V536" fill="none" stroke="#4B2C6B" strokeWidth="76" strokeLinecap="round" />
        <path d={BRAND_SPARK_PATH} fill="#EE9A3C" />
      </g>
    </svg>
  );
}

/** The AI spark: a four-point star filling the space above the U's shorter right arm. */
const BRAND_SPARK_PATH =
  'M560.0 368.0 C566.6 411.2 576.8 421.4 620.0 428.0 C576.8 434.6 566.6 444.8 560.0 488.0 C553.4 444.8 543.2 434.6 500.0 428.0 C543.2 421.4 553.4 411.2 560.0 368.0Z';

interface BrandWordmarkProps {
  className?: string;
  /** Font size in px (20 in headers, 28 on auth cards). */
  size?: number;
}

/**
 * "UniPostudio" in one style: ink, DM Serif Display, no two-tone accent split.
 * The brand name is never translated.
 */
export function BrandWordmark({ className = '', size = 20 }: BrandWordmarkProps): JSX.Element {
  return (
    <span className={['font-display text-[var(--ink)] leading-none', className].join(' ')} style={{ fontSize: size }} translate="no">
      UniPostudio
    </span>
  );
}
